import {
  IProduct,
  IProductResSchema,
  MinCommitConfigTypeEnum,
  ProductStatusEnum,
  ProductTypeEnum,
} from '@monetize/types/app';
import debounce from 'lodash/debounce';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { apiGetAllList } from '../../../../../api/axios';
import { useGetListData } from '../../../../../api/queryUtils';
import {
  MButton,
  MFlex,
  MFormField,
  MText,
} from '../../../../../components/Monetize';
import {
  MCustomMultiSelect,
  MCustomMultiSelectRef,
} from '../../../../../components/Monetize/MCustomSelect/MCustomMultiSelect';
import { MCustomSelect } from '../../../../../components/Monetize/MCustomSelect/MCustomSelect';
import { useCustomSelectValues } from '../../../../../components/Monetize/MCustomSelect/useCustomSelectValue';
import { MinCommitConfigTypeSelectOptions } from '../../../../../constants/products';
import { PRODUCTS_ITEM_DEFAULT_PAGER } from './RatePriceUtils';

interface RateMinimumCommitConfigProps {
  product: IProduct;
  isLoading?: boolean;
  isLocked?: boolean;
  isDisabled?: boolean;
  /** True if the rate is already saved in database */
  isExistingRate?: boolean;
}
const RateMinimumCommitConfig = React.forwardRef<
  HTMLInputElement | null,
  RateMinimumCommitConfigProps
>(
  (
    {
      product,
      isLoading,
      isLocked,
      isDisabled = false,
      isExistingRate = false,
    }: RateMinimumCommitConfigProps,
    forwardedRef: React.ForwardedRef<HTMLInputElement | null>,
  ) => {
    const {
      control,
      formState: { errors, dirtyFields },
      watch,
      setValue,
    } = useFormContext();
    const watchMinCommitConfig = watch(`minCommitConfig`);

    const requiresProductSelection =
      watchMinCommitConfig?.type ===
        MinCommitConfigTypeEnum.SPECIFIC_USAGE_PRODUCTS ||
      watchMinCommitConfig?.type ===
        MinCommitConfigTypeEnum.EXCLUDE_SPECIFIC_USAGE_PRODUCTS;

    const [query, setQuery] = useState<string>('');
    const [searchTerm, setSearchTerm] = useState<string>('');
    const { data: productList, isLoading: productListLoading } =
      useGetListData<IProduct>(
        'productCatalogProducts',
        {
          config: PRODUCTS_ITEM_DEFAULT_PAGER,
          filters: {
            name: { contains: searchTerm },
            status: ProductStatusEnum.ACTIVE,
            productType: {
              in: [ProductTypeEnum.USAGE],
            },
            sort: 'name',
          },
        },
        {
          placeholderData: (previousData, previousQuery) => previousData,
          refetchOnWindowFocus: false,
          enabled: requiresProductSelection,
        },
      );

    const setQueryDebounced = useMemo(() => debounce(setSearchTerm, 300), []);

    const setFormValue = (value: string[]) => {
      setValue(`minCommitConfig.productIds`, value, {
        shouldValidate: true,
        shouldDirty: true,
      });
    };

    const customSelectRef = useRef<MCustomMultiSelectRef>(undefined);

    const {
      internalValue,
      isLoading: isLoadingSavedValue,
      placeholder,
      onInternalValueChange,
    } = useCustomSelectValues<IProductResSchema>({
      value: watchMinCommitConfig?.productIds,
      setValue: setFormValue,
      getByIds: async (ids: string[]) =>
        ids.length > 0
          ? apiGetAllList<IProductResSchema>('/api/products', {
              filters: { id: { in: ids } },
            })
          : [],
      label: 'Products',
    });

    useEffect(() => {
      // This ensures that MCustomMultiSelect knows about the date change
      // otherwise it had stale data when opened the first time
      customSelectRef.current?.updateItems(internalValue);
    }, [internalValue]);

    const onChangeQuery = (q: string): void => {
      if (q !== query) {
        setQuery(q);
        setQueryDebounced(q);
      }
    };

    const onClearAll = () => {
      customSelectRef.current?.clearAll();
      onInternalValueChange([]);
    };

    return (
      <MFlex gap="4" alignItems="center">
        <MFormField error={errors.minCommitConfig?.type} label="Apply To">
          <Controller
            name="minCommitConfig.type"
            control={control}
            render={({ field: { onChange, ...rest } }) => (
              <MCustomSelect
                items={MinCommitConfigTypeSelectOptions}
                isDisabled={isLoading}
                inputProps={{
                  variant:
                    isExistingRate && dirtyFields?.minCommitConfig?.type
                      ? 'unsaved'
                      : undefined,
                }}
                {...rest}
                onChange={(value) => {
                  onChange(value);
                  // "All Usage Products" has no explicit product list — clear any selection
                  if (value === MinCommitConfigTypeEnum.USAGE_PRODUCTS) {
                    onClearAll();
                    setValue('minCommitConfig.productIds', undefined, {
                      shouldValidate: true,
                      shouldDirty: true,
                    });
                  }
                }}
              />
            )}
          />
        </MFormField>
        {requiresProductSelection && (
          <MFormField
            data-testid="min-commit-product-select"
            isRequired={!isLocked}
            error={(errors.minCommitConfig as any)?.productIds}
            label="Products"
          >
            <Controller
              name="minCommitConfig.productIds"
              control={control}
              render={({ field: { onChange, ref, ...rest } }) => (
                <MCustomMultiSelect
                  ref={(refEvent: any) => {
                    ref(refEvent);
                    customSelectRef.current = refEvent;
                  }}
                  isLazy
                  items={productList?.content}
                  itemTitle="name"
                  itemValue="id"
                  showQueryInput
                  multiple
                  onChange={(val) => {
                    onInternalValueChange(val as IProductResSchema[]);
                  }}
                  useExternalQuery
                  returnItem
                  externalQuery={query}
                  onChangeQuery={onChangeQuery}
                  {...rest}
                  value={internalValue}
                  isDisabled={isDisabled}
                  loading={productListLoading || isLoadingSavedValue}
                  closeButtonText="Apply"
                  placeholder={placeholder}
                  inputProps={{
                    variant:
                      isExistingRate && dirtyFields?.minCommitConfig?.productIds
                        ? 'unsaved'
                        : 'outline',
                    _placeholder: { color: 'tPurple.dark' },
                    _disabled: {
                      // By default we hide placeholder when disabled, but here we want it visible
                      _placeholder: {
                        color: 'tGray.darkPurple',
                      },
                    },
                  }}
                  renderPreContent={() => (
                    <MFlex justifyContent="space-between" mx="2" mt="2">
                      <MText fontStyle="italic" fontSize="xs"></MText>
                      <MButton
                        variant="tertiary"
                        size="xs"
                        role="button"
                        onClick={onClearAll}
                      >
                        Clear All
                      </MButton>
                    </MFlex>
                  )}
                />
              )}
            />
          </MFormField>
        )}
      </MFlex>
    );
  },
);

export default RateMinimumCommitConfig;
